/**
 * 「我的座位」面板：暂离 / 换座 / 起立 / 离开房间。
 *
 * 以前坐下之后就被锁在座位上：服务端早就支持起立和暂离，客户端从来没有一个地方发过这两条；
 * 唯一的出口是顶栏「离开」，它离开的是整个房间。
 *
 * ── 从哪儿打开（UI-SPEC §10「座位操作」）────────────────────────
 *   1. 坐着、这一页有行动权时，点顶栏「离开」；旁观者和影子页仍是「离开 → 确认离开」。
 *   2. 点牌桌上自己的座位（轮到自己时不响应：面板不能盖住一个正在倒计时的决定）。
 *
 * ── 四行，从上到下 ─────────────────────────────────────────────
 *   1. 暂离 / 取消暂离 / 回到牌局（唯一 Primary）/ 买分（筹码用完时）
 *   2. 换座（本手发到牌、买分或转账在途、没空位时灰掉，行内一句 12px 原因）
 *   3. 起立 / 这手打完起立（已点、在等这手打完，灰掉）
 *   4. 离开房间 —— Destructive；面板本身就是第二步，所以点一下就走（与顶栏两段式同样两下）
 *
 * 局内点暂离 / 起立会排到这手打完：面板关掉时冒一句「这手打完暂离 / 起立」，否则牌桌上
 * 什么都没变，会以为没点上。
 */
import { useCallback, useEffect } from 'react'
import type { ReactNode } from 'react'

import { useUiStore } from '../store/index.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'

import { errorText } from './errorText.js'
import {
  seatSelfActions,
  seatSelfFeedback,
  seatSelfMessage,
  type SeatSelfIntent,
  type SeatSelfModel,
} from './roomDerive.js'
import { useRoomLink } from './roomLink.js'
import { SheetShell } from './SheetShell.js'
import { useLeaveRoom } from './useLeaveRoom.js'
import './room.css'

export interface SeatSelfSheetProps {
  /** 离开房间后的去向（一般是「回大厅」），与顶栏同一个 */
  readonly onLeave?: () => void
}

export function SeatSelfSheet({ onLeave }: SeatSelfSheetProps): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const openSheet = useUiStore((s) => s.openSheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const showToast = useUiStore((s) => s.showToast)
  const view = useNetStore(selectNetView)
  const link = useRoomLink()
  const leave = useLeaveRoom(onLeave, view)
  const model = seatSelfActions(view)
  const open = sheet === 'seatSelf'
  const gone = model === null

  useSeatErrorToast()

  // 面板开着时座位没了（起立生效 / 被移出 / 牌局结算）：顺手把开合状态也清掉，
  // 否则下次坐下时它会凭空弹出来
  useEffect(() => {
    if (open && gone) closeSheet()
  }, [open, gone, closeSheet])

  const pick = useCallback(
    (intent: SeatSelfIntent): void => {
      const current = seatSelfActions(useNetStore.getState().view)
      if (current === null) return
      if (intent === 'topUp') {
        openSheet('topUp')
        return
      }
      if (intent === 'change') {
        openSheet('seatChange')
        return
      }
      closeSheet()
      if (intent === 'leave') {
        leave()
        return
      }
      const msg = seatSelfMessage(intent)
      if (msg !== null) link.send(msg)
      const note = seatSelfFeedback(intent, current)
      if (note !== null) showToast(note, 'info')
    },
    [closeSheet, leave, link, openSheet, showToast],
  )

  if (!open || model === null) return null
  return <SeatSelfPanel model={model} onPick={pick} onClose={closeSheet} />
}

export interface SeatSelfPanelProps {
  readonly model: SeatSelfModel
  readonly onPick: (intent: SeatSelfIntent) => void
  readonly onClose: () => void
}

const REST_LABEL: Readonly<Record<SeatSelfModel['rest'], string>> = {
  sitOut: '暂离',
  cancelSitOut: '取消暂离',
  return: '回到牌局',
  topUp: '买分',
}

/**
 * 面板本体。与外壳分开：node 测试里 zustand 只读初值，挂在 store 上的开合那一层静态渲染不出来，
 * 本体只吃 props，哪几行、哪一颗是 Primary、哪一颗是 Destructive 都能直接钉住。
 */
export function SeatSelfPanel({ model, onPick, onClose }: SeatSelfPanelProps): ReactNode {
  const primary = model.rest === 'return'
  return (
    <SheetShell label="我的座位" onClose={onClose}>
      <header className="sheet__head">
        <strong>我的座位</strong>
        <span className="sheet__sub num">{model.seat + 1} 号位</span>
      </header>

      <div className="seat-self" role="group" aria-label="座位操作">
        {primary ? (
          <button type="button" className="btn btn--cta pressable" onClick={() => onPick('return')}>
            回到牌局
          </button>
        ) : (
          <button type="button" className="btn btn--quiet transfer-menu__item" onClick={() => onPick(model.rest)}>
            {REST_LABEL[model.rest]}
          </button>
        )}

        <button
          type="button"
          className="btn btn--quiet transfer-menu__item"
          disabled={model.changeBlocked !== null}
          aria-describedby={model.changeBlocked === null ? undefined : 'seat-self-change-why'}
          onClick={() => onPick('change')}
        >
          换座
          {model.changeBlocked !== null && (
            <span className="seat-self__why" id="seat-self-change-why">
              {model.changeBlocked}
            </span>
          )}
        </button>

        <button
          type="button"
          className="btn btn--quiet transfer-menu__item"
          disabled={model.standPending}
          onClick={() => onPick('stand')}
        >
          {model.standPending ? '这手打完起立' : '起立'}
        </button>

        <button
          type="button"
          className="btn btn--quiet transfer-menu__item btn--destructive"
          onClick={() => onPick('leave')}
        >
          离开房间
        </button>
      </div>
    </SheetShell>
  )
}

/**
 * 座位类命令被拒 → Toast。等待室有一个总的拒绝提示，可牌局进行中它不在，
 * 换座被人抢先、这手还没打完之类的拒绝就只落在 lastError 里，点了没反应。
 */
const SEAT_ERRORS = new Set<string>([
  'E_SEAT_TAKEN',
  'E_SEAT_INVALID',
  'E_SEAT_CHANGE_MIDHAND',
  'E_ALREADY_SEATED',
  'E_NOT_SEATED',
])

function useSeatErrorToast(): void {
  const lastError = useNetStore((s) => s.lastError)
  const clearError = useNetStore((s) => s.clearError)
  const showToast = useUiStore((s) => s.showToast)

  useEffect(() => {
    if (lastError === null || !SEAT_ERRORS.has(lastError.code)) return
    showToast(errorText(lastError.code, lastError.message), 'warn')
    clearError()
  }, [lastError, clearError, showToast])
}

export default SeatSelfSheet
